<script lang="ts">
	import type { AdId } from '$lib/services/advertising/types';

	import { page } from '$app/state';
	import AdvertisingTemplate from '$lib/components/advertising/AdvertisingTemplate.svelte';
	import { getAdFormatFromDeviceWidth } from '$lib/components/advertising/utils';
	import { type AdFormats, getOrangeAdId, getOrangeAdPageId } from '$lib/services/advertising';
	import { loadOrangeConfig } from '$lib/services/advertising/orange/config';
	import { getTargeting } from '$lib/services/advertising/tracking';
	import { useSplunkRum } from '$lib/services/tracking/splunk/splunkRum';
	import { getSearchParamsStore } from '$lib/services/urls/searchParams';
	import { onDestroy, onMount } from 'svelte';

	interface Props {
		adFormat: AdFormats;
		adId: AdId;
		showAdByDefault: boolean;
		adIdForTests: AdId;
		onAdAvailabilityChange?: (isAvailable: boolean) => void;
	}

	const {
		adFormat,
		adId,
		showAdByDefault,
		adIdForTests,
		onAdAvailabilityChange,
	}: Props = $props();

	const searchParams = getSearchParamsStore();
	const { itineraryResultsStore, poisStore, waypointsInfo } = page.data;

	// No need for reactivity here as any change should trigger a full component reload
	const orangeAdId = getOrangeAdId(adId, $searchParams, page);

	let slotIsCreated = false;
	// Separate mounting and creating Ad slot for a better cleaning and mounting process, ensuring the slot is created only once
	let isOrangeConfigLoaded = $state(false);

	async function ensureTargeting() {
		if (!isOrangeConfigurationLoaded()) {
			useSplunkRum((SplunkRum) => {
				SplunkRum.setGlobalAttributes({
					adgw_loaded: false,
					ad_id: adId,
					ad_format: JSON.stringify(adFormat),
				});
			});
			return;
		}

		const targeting = getTargeting({
			poisStore: $poisStore,
			searchParams: $searchParams,
			route: $itineraryResultsStore?.routes[$searchParams.selectedRoute ?? 0],
			waypoints: $itineraryResultsStore?.waypoints,
			arrivalAddress: $itineraryResultsStore?.arrivalAddress,
			departureAddress: $itineraryResultsStore?.departureAddress,
			searchWaypoints: waypointsInfo,
			page,
			adId,
		});

		if (!targeting || !orangeAdId) return;

		if (!slotIsCreated) {
			await window.ADGW?.createSlot(orangeAdId).catch((error) => {
				useSplunkRum((SplunkRum) => {
					SplunkRum.setGlobalAttributes({
						slot_created: false,
						slot: orangeAdId,
						error_message: error,
					});
				});
			});
			slotIsCreated = true;
		}

		await window.ADGW?.setSlotTargetings(orangeAdId, JSON.stringify(targeting)).catch((error) => {
			useSplunkRum((SplunkRum) => {
				SplunkRum.setGlobalAttributes({
					targeting_setted: false,
					slot: orangeAdId,
					targetings: JSON.stringify(targeting),
					error_message: error,
				});
			});
		});
		await window.ADGW?.displaySlot(orangeAdId).catch((error) => {
			useSplunkRum((SplunkRum) => {
				SplunkRum.setGlobalAttributes({
					display_slot: false,
					slot: orangeAdId,
					error_message: error,
				});
			});
		});
	}

	function isOrangeConfigurationLoaded() {
		return !!window.adgw_data || !!window.ADGW;
	}

	async function loadAd() {
		const format = getAdFormatFromDeviceWidth(adFormat);

		if (!format || !window.ADGW) {
			useSplunkRum((SplunkRum) => {
				SplunkRum.setGlobalAttributes({
					adgw_loaded: false,
					ad_id: adId,
					ad_format: JSON.stringify(adFormat),
				});
			});
			return;
		}

		const orangePageId = getOrangeAdPageId(adId, $searchParams, page);

		try {
			await loadOrangeConfig(orangePageId);
			isOrangeConfigLoaded = true;
		} catch (error) {
			useSplunkRum((SplunkRum) => {
				SplunkRum.setGlobalAttributes({
					slot_already_created: true,
					slot: orangeAdId,
					error_message: `${error}`,
				});
			});
		}
	}

	async function removeSlot() {
		if (!window.ADGW) {
			return;
		}

		await window.ADGW.deleteSlot(orangeAdId).catch((error) => {
			useSplunkRum((SplunkRum) => {
				SplunkRum.setGlobalAttributes({
					slot_deleted: false,
					slot: orangeAdId,
					error_message: error,
				});
			});
		});
		slotIsCreated = false;
		isOrangeConfigLoaded = false;
	}

	onMount(() => {
		loadAd();
	});

	onDestroy(() => {
		// onDestroy doesn't support async function, we must use an IIFE to execute correctly the async function
		(async () => {
			await removeSlot();
		})();
	});

	$effect(() => {
		if (isOrangeConfigLoaded) {
			ensureTargeting();
		}
	});
</script>

<AdvertisingTemplate {adFormat} adId={orangeAdId} {showAdByDefault} {adIdForTests} {onAdAvailabilityChange} />
